import React, { useEffect, useMemo } from 'react';
import {
	getAssetDetail,
	getAssetRisk,
	getAssetRiskHistory,
	getInspectionAttributes,
} from '@innovyze/lib-am-common/Actions';
import { NotFoundPage, getSubscriptions, useTzDateTime } from '@innovyze/stylovyze';
import { useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import { AssetDetails as AssetDetailsComponent } from '@innovyze/lib-am-common/Components';
import { fullAccess } from '@innovyze/shared-utils';
import { selectAssetBackLabel } from '@Selectors';
import { useAssetTypeSummary } from '@Hooks/assetSchemaHooks';
import { decodeRouteParam } from '@Utils/urlFormatting';

// A link built from a missing value template-literals it into the path as the string
// "undefined", which slips past a plain `!= undefined` check.
const isMissingRouteParam = (value: string | undefined): boolean => !value || value === 'undefined' || value === 'null';

export const AssetDetails = () => {
	const rawParams: { assetType: string; assetId: string } = useParams();
	const params = useMemo(
		() => ({
			assetType: decodeRouteParam(rawParams.assetType),
			assetId: decodeRouteParam(rawParams.assetId),
		}),
		[rawParams.assetType, rawParams.assetId],
	);
	const hasValidParams = !isMissingRouteParam(params.assetType) && !isMissingRouteParam(params.assetId);
	const history = useHistory();
	const dispatch = useDispatch();

	const formatDateUTC = useTzDateTime({ timeZone: 'UTC' }).formatDate;

	const subscriptions = getSubscriptions();
	const hasFullAccess = fullAccess(subscriptions);

	// Display hooks use the open flag, so this resolves regardless of the catalog rollout.
	const { data: assetTypeSummary, status: schemaStatus } = useAssetTypeSummary(hasValidParams ? params.assetType : '');

	const assetBackLabel = selectAssetBackLabel();

	const systemType = assetTypeSummary?.systemTypeId;

	useEffect(() => {
		// Wait for the schema so the detail request carries the system type.
		if (hasValidParams && schemaStatus !== 'pending') {
			dispatch(
				getAssetDetail({
					assetId: params.assetId,
					assetType: params.assetType,
					formatDateUTC,
					systemType: systemType,
				}),
			);

			const payload2 = {
				assetId: params.assetId,
				assetType: params.assetType,
			};
			dispatch(getAssetRisk(payload2));
			dispatch(getInspectionAttributes(payload2));
			dispatch(getAssetRiskHistory(payload2));
		}
	}, [params, systemType, schemaStatus]);

	const back = () => {
		history.goBack();
	};

	if (!hasValidParams) {
		return <NotFoundPage />;
	}

	return (
		<AssetDetailsComponent
			assetId={params.assetId}
			back={{
				action: back,
				label: assetBackLabel,
			}}
			assetType={params.assetType}
			adminFullAccess={hasFullAccess}
			systemType={systemType}
		/>
	);
};
